import { useState } from "react";
import { message, Modal, notification } from "antd";
import { useTranslation } from "react-i18next";
import { getFirebaseIDToken } from "../functions/firebase/firebase";
import dayjs from "dayjs";
import { firestore } from "../configs/FirebaseConfig";
import { doc, updateDoc } from "firebase/firestore";
import { FIRESTORE_INVOICES_TABLE, FIRESTORE_REGIONS_TABLE } from "../constants/FirestoreConstant";

// Flag to control sync behavior:
// true = directly sync to Exact (current behavior)
// false = only change status to "ready_to_sync" for cron job to process
const DIRECT_EXACT_SYNC = false;

export const useBulkExactSync = () => {
  const { t } = useTranslation();

  const [bulkExactModalVisible, setBulkExactModalVisible] = useState(false);
  const [bulkExactProgress, setBulkExactProgress] = useState(0);
  const [bulkExactCurrentItem, setBulkExactCurrentItem] = useState("");
  const [bulkExactResults, setBulkExactResults] = useState({ successful: [], failed: [] });
  const [bulkExactProcessing, setBulkExactProcessing] = useState(false);

  const getFilteredItemsDeliveryDate = (items, dateRange, selectedRows) => {
    if (selectedRows.length > 0) {
      return selectedRows;
    } else if (dateRange && dateRange.length > 0) {
      const startDate = dateRange[0].format("YYYY-MM-DD");
      const endDate = dateRange[1].format("YYYY-MM-DD");
      return items.filter((item) => {
        return dayjs(item.farmorderDeliveryDate).isBetween(startDate, endDate, undefined, "[]");
      });
    }
    return [];
  };

  const handleBulkSendToExact = async ({
    items,
    dateRange,
    selectedRows,
    filterKey, // 'exactOrderNumber' for orders, 'exactInvoiceId' for invoices
    sendFunction, // e.g. sendOrderToExactInvoice
    transformData, // optional data transformation function
    confirmTitle,
    confirmMessage,
    itemType = "item", // 'order' or 'invoice'
  }) => {
    const filteredItems = getFilteredItemsDeliveryDate(items, dateRange, selectedRows);

    if (filteredItems.length === 0) {
      message.error(t(`notifications.no_${itemType}s_selected`));
      return;
    }

    // Filter out items that already have exact ID
    const itemsToProcess = filteredItems.filter((item) => !item[filterKey]);

    if (itemsToProcess.length === 0) {
      message.info(t(`notifications.all_${itemType}s_already_in_exact`));
      return;
    }

    // Show confirmation
    Modal.confirm({
      title: confirmTitle,
      content: DIRECT_EXACT_SYNC
        ? t(confirmMessage, {
            count: itemsToProcess.length,
            skipped: filteredItems.length - itemsToProcess.length,
          })
        : t(confirmMessage + "_ready_to_sync", {
            count: itemsToProcess.length,
            skipped: filteredItems.length - itemsToProcess.length,
          }),
      okText: t("yes"),
      cancelText: t("no"),
      onOk: async () => {
        if (DIRECT_EXACT_SYNC) {
          setBulkExactModalVisible(true);
          setBulkExactProgress(0);
          setBulkExactResults({ successful: [], failed: [] });
          setBulkExactProcessing(true);
          processBulkExactItems(itemsToProcess, sendFunction, transformData, itemType);
        } else {
          // Status change mode - no modal, just process and show notification
          await processStatusChangeItemsQuiet(itemsToProcess, itemType);
        }
      },
    });
  };

  const processBulkExactItems = async (itemsToProcess, sendFunction, transformData, itemType) => {
    const results = { successful: [], failed: [] };
    const token = await getFirebaseIDToken();

    for (let i = 0; i < itemsToProcess.length; i++) {
      const item = itemsToProcess[i];
      const displayName = item.farmorderCustomerName + " - " + (item.farmorderId || item.id);
      setBulkExactCurrentItem(displayName);

      try {
        const dataToSend = transformData ? transformData(item) : item;
        await sendFunction(dataToSend, token);

        results.successful.push({
          id: item.farmorderId || item.id,
          customerName: item.farmorderCustomerName,
        });
      } catch (error) {
        results.failed.push({
          id: item.farmorderId || item.id,
          customerName: item.farmorderCustomerName,
          error: error.message || error,
        });
      }

      // Update progress
      const progressPercent = Math.round(((i + 1) / itemsToProcess.length) * 100);
      setBulkExactProgress(progressPercent);

      // Small delay to prevent overwhelming the API
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setBulkExactResults(results);
    setBulkExactProcessing(false);
    setBulkExactCurrentItem("");
  };

  const processStatusChangeItems = async (itemsToProcess, itemType) => {
    const results = { successful: [], failed: [] };

    for (let i = 0; i < itemsToProcess.length; i++) {
      const item = itemsToProcess[i];
      const displayName = item.farmorderCustomerName + " - " + (item.farmorderId || item.id);
      setBulkExactCurrentItem(displayName);

      try {
        // Update status to "ready_to_sync" in Firestore
        const itemRef = doc(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`, item.id);
        await updateDoc(itemRef, {
          status: "ready_to_sync",
          updatedAt: dayjs().unix(),
        });

        results.successful.push({
          id: item.farmorderId || item.id,
          customerName: item.farmorderCustomerName,
        });
      } catch (error) {
        results.failed.push({
          id: item.farmorderId || item.id,
          customerName: item.farmorderCustomerName,
          error: error.message || error,
        });
      }

      // Update progress
      const progressPercent = Math.round(((i + 1) / itemsToProcess.length) * 100);
      setBulkExactProgress(progressPercent);

      // Small delay to prevent overwhelming Firestore
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    setBulkExactResults(results);
    setBulkExactProcessing(false);
    setBulkExactCurrentItem("");
  };

  const processStatusChangeItemsQuiet = async (itemsToProcess, itemType) => {
    const results = { successful: [], failed: [] };

    for (let i = 0; i < itemsToProcess.length; i++) {
      const item = itemsToProcess[i];

      try {
        // Update status to "ready_to_sync" in Firestore
        const itemRef = doc(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`, item.id);
        await updateDoc(itemRef, {
          status: "ready_to_sync",
          updatedAt: dayjs().unix(),
        });

        results.successful.push({
          id: item.farmorderId || item.id,
          customerName: item.farmorderCustomerName,
        });
      } catch (error) {
        results.failed.push({
          id: item.farmorderId || item.id,
          customerName: item.farmorderCustomerName,
          error: error.message || error,
        });
      }

      // Small delay to prevent overwhelming Firestore
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Show notification immediately
    showStatusChangeNotification(results, itemType);
  };

  const showStatusChangeNotification = (results, itemType) => {
    const successCount = results.successful.length;
    const failCount = results.failed.length;

    const title =
      failCount === 0
        ? t(`notifications.bulk_status_change_${itemType}s_success`, { count: successCount })
        : t(`notifications.bulk_status_change_${itemType}s_partial`, { success: successCount, failed: failCount });

    const content = (
      <div>
        {successCount > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <strong style={{ color: "#52c41a" }}>
              {t(`successful_${itemType}s`)} ({successCount}):
            </strong>
            <ul style={{ margin: "4px 0", paddingLeft: "20px", maxHeight: "100px", overflowY: "auto" }}>
              {results.successful.map((item, index) => (
                <li key={index}>
                  {item.customerName} - {item.id}
                </li>
              ))}
            </ul>
          </div>
        )}

        {failCount > 0 && (
          <div>
            <strong style={{ color: "#ff4d4f" }}>
              {t(`failed_${itemType}s`)} ({failCount}):
            </strong>
            <ul style={{ margin: "4px 0", paddingLeft: "20px", maxHeight: "100px", overflowY: "auto" }}>
              {results.failed.map((item, index) => (
                <li key={index}>
                  {item.customerName} - {item.id}: {item.error}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );

    notification.destroy();

    notification[failCount === 0 ? "success" : "warning"]({
      message: title,
      description: content,
      duration: 0,
      style: { width: "500px" },
    });
  };

  const closeBulkExactModal = (itemType = "item") => {
    setBulkExactModalVisible(false);

    if (bulkExactResults.successful.length > 0 || bulkExactResults.failed.length > 0) {
      // Show summary notification for direct sync mode
      const successCount = bulkExactResults.successful.length;
      const failCount = bulkExactResults.failed.length;

      const title =
        failCount === 0
          ? t(`notifications.bulk_exact_${itemType}s_success`, { count: successCount })
          : t(`notifications.bulk_exact_${itemType}s_partial`, { success: successCount, failed: failCount });

      const content = (
        <div>
          {successCount > 0 && (
            <div style={{ marginBottom: "8px" }}>
              <strong style={{ color: "#52c41a" }}>
                {t(`successful_${itemType}s`)} ({successCount}):
              </strong>
              <ul style={{ margin: "4px 0", paddingLeft: "20px", maxHeight: "100px", overflowY: "auto" }}>
                {bulkExactResults.successful.map((item, index) => (
                  <li key={index}>
                    {item.customerName} - {item.id}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {failCount > 0 && (
            <div>
              <strong style={{ color: "#ff4d4f" }}>
                {t(`failed_${itemType}s`)} ({failCount}):
              </strong>
              <ul style={{ margin: "4px 0", paddingLeft: "20px", maxHeight: "100px", overflowY: "auto" }}>
                {bulkExactResults.failed.map((item, index) => (
                  <li key={index}>
                    {item.customerName} - {item.id}: {item.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );

      notification.destroy();

      notification[failCount === 0 ? "success" : "warning"]({
        message: title,
        description: content,
        duration: 0,
        style: { width: "500px" },
      });
    }
  };

  return {
    // State
    bulkExactModalVisible,
    bulkExactProgress,
    bulkExactCurrentItem,
    bulkExactResults,
    bulkExactProcessing,

    // Actions
    handleBulkSendToExact,
    closeBulkExactModal,
  };
};
